<template>
  <div class="footer-offset renedian-light-sand--background d-flex flex-column align-center">

    <RippedHeader :breadcrumb="headerBreadcrumb" :title="headerTitle" :carousel="regionImages" />
    
    <!-- Tour Description -->
    <div class="my-12 text-center width-95-vw">
      <div class="barlow-condensed-bold-italic renedian-forest-green--text text-uppercase mb-4 font-size-xl">{{ regionData.slogan }}</div>
      <div class="barlow-regular grey--text text--darken-3 font-size-sm line-height-5">{{ regionData.description }}</div>
      <!-- Private Tour Notice -->
      <div v-if="regionData.privateTour" class="mt-6 d-flex align-center justify-center">
        <v-chip color="purple" text-color="white" small class="mr-2">
          <v-icon small left>mdi-lock</v-icon>
          Private Tours Available
        </v-chip>
      </div>
    </div>

    <!-- Tours List -->
    <div v-if="!loading && displayTemplates.length" class="d-flex flex-column align-center pb-12 width-95-vw">
      <div v-for="group in templateGroups" :key="group.groupKey" class="width-100 d-flex flex-column align-center">
        
        <!-- Group Header (only for grouped view) -->
        <div v-if="group.title" class="width-100 d-flex flex-column align-center my-12">
          <div class="d-flex jost-bold renedian-grey--text text-uppercase text-center font-size-lg">
            {{ group.title }}
          </div>
        </div>
        
        <!-- Desktop Cards -->
        <div
          v-for="template in group.templates"
          :key="`${template.id}-${group.groupKey}-desktop`"
          class="d-none d-md-flex flex-row align-start width-95-vw mb-12 pa-6"
          rounded="xl"
        >
          <!-- Left: Photo + Badge -->
          <div class="width-35 position-relative">
            <img
              class="width-100 d-block tour-thumb hover-pointer lifted-photo lifted-photo-right"
              :src="$img(template.images?.thumbnail, 800)"
              :alt="template.name"
              loading="lazy"
              @click="viewTemplate(template)"
            />
            <img 
              v-if="template.images?.badge" 
              :src="$img(template.images.badge, 400)" 
              :alt="`${template.name} badge`"
              style="top: 50%; left: -5%; transform: translateY(-50%);"
              class="position-absolute d-none d-md-block width-40"
              loading="lazy"
              @click="viewTemplate(template)"
            />
          </div>

          <!-- Right: Content -->
          <div class="flex-1 d-flex flex-column align-center justify-center pl-12 pr-6">
            <div class="barlow-condensed-bold text-uppercase renedian-sand-green--text hover-pointer font-size-xl" @click="viewTemplate(template)">
              {{ template.name }}
            </div>
            <div class="barlow-regular mt-4 grey--text text--darken-3 font-size-sm">
              {{ getTemplateDescription(template) }}
            </div>
            <RidingBar :riding="template.riding" :duration="template.duration" class="mt-6"/>
          </div>

          <!-- Right: Price + Explore -->
          <div class="d-flex flex-column align-center width-15">
            <div class="d-flex flex-column align-center">
              <div v-if="template.pricing?.tour?.base !== undefined" @click="viewTemplate(template)" class="mb-4 position-relative width-100 cursor-pointer">
                <img class="width-100" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FPrice%20Tag%20-%20with%20text.svg?alt=media&token=f5ebe485-2318-4942-af7f-683aa8de78ad" alt="" />
                <div class="position-absolute" style="bottom: 1vw; right: 1vw;">
                  <span class="d-none d-md-block d-lg-none font-size-md white--text font-weight-bold">{{ template.pricing.tour.base | price }}</span>
                  <span class="d-md-none d-lg-block font-size-lg white--text font-weight-bold">{{ template.pricing.tour.base | price }}</span>
                </div>
              </div>
              <img @click="viewTemplate(template)" class="cursor-pointer" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FEXPLORE%20button.svg?alt=media&token=1f03be4f-4232-4e1b-b377-565d52f3336c" alt="" />
            </div>
          </div>
        </div>

        <!-- Mobile Cards -->
        <v-card
          v-for="template in group.templates"
          :key="`${template.id}-${group.groupKey}-mobile`"
          class="d-flex d-md-none flex-column align-center justify-center pa-6 mb-12 width-95-vw white"
          rounded="xl"
        >
          <div class="position-relative width-100" :class="{ 'hover-pointer': group.groupKey === 'paved' }" @click="group.groupKey === 'paved' ? viewTemplate(template) : null">
            <img class="width-100 rounded-xl" :src="$img(template.images?.thumbnail, 800)" :alt="template.name" loading="lazy" />
            <img v-if="template.images?.badge" :src="$img(template.images.badge, 400)" :alt="`${template.name} badge`" class="position-absolute" style="width: 32vw; left: -2vw; bottom: -8vw;" loading="lazy" />
          </div>
          <div class="d-flex flex-column align-center text-center mt-12">
            <div 
              class="barlow-condensed-bold text-uppercase renedian-sand-green--text mb-2 width-100 font-size-huge" 
              :class="{ 'hover-pointer': group.groupKey === 'paved' || group.groupKey === 'gravel' }"
              style="border-bottom: 3px solid #5a6660;" 
              @click="(group.groupKey === 'paved' || group.groupKey === 'gravel') ? viewTemplate(template) : null"
            >
              {{ template.name }}
            </div>
            <div class="barlow-condensed-regular renedian-burnt-orange--text text-uppercase font-size-lg">
              {{ template.schedule?.duration || template.duration }} days
            </div>
            <div class="barlow-regular mt-3 grey--text text--darken-3 font-size-sm">
              {{ getTemplateDescription(template, true) }}
            </div>
          </div>
          <RidingBar :riding="template.riding" :duration="template.duration" class="mt-6"/>
          <div class="d-flex flex-column align-center my-4 explore-button">
            <img style="height: 40px" @click="viewTemplate(template)" class="cursor-pointer" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FEXPLORE%20button.svg?alt=media&token=1f03be4f-4232-4e1b-b377-565d52f3336c" alt="" />
          </div>
        </v-card>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="py-12 d-flex justify-center">
      <v-progress-circular indeterminate></v-progress-circular>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !displayTemplates.length" class="py-12 d-flex justify-center">
      <div class="barlow-regular grey--text">No tours found for this region.</div>
    </div>
  </div>
</template>

<script>
import { metaMixin } from '@/mixins/metaMixin'
import { getFirestore, collection, getDocs, getDoc, doc, query, where, limit } from 'firebase/firestore'
import RidingBar from '@/components/RidingBar.vue'
import RippedHeader from '@/components/RippedHeader.vue'
import { emitPrerenderReady } from '@/utils/prerender'
import { fetchPublicCache } from '@/constants/publicCache'

export default {
  name: 'Region',
  mixins: [metaMixin],
  components: {
    RidingBar,
    RippedHeader
  },
  data() {
    return {
      loading: true,
      templates: [],
      regionDoc: null,
      // Stable carousel state
      carouselIndex: 0,
      regionImagesLocal: []
    }
  },
  computed: {
    regionData() {
      return this.regionDoc || {}
    },
    carouselHeight() {
      // 7:3 aspect ratio based on responsive width
      const isMdUp = this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.mdAndUp
      return isMdUp ? 'calc(70vw * 3 / 7)' : 'calc(100vw * 3 / 7)'
    },
    regionImages() {
      const images = this.regionData && this.regionData.images
      if (!images) return []
      const rawArray = Array.isArray(images)
        ? images
        : (typeof images === 'object' ? Object.values(images) : [])
      return rawArray
        .map(item => {
          if (typeof item === 'string') return { url: item, caption: '' }
          if (item && typeof item === 'object') {
            const url = item.url || item.src || item.image || item.path || ''
            const caption = item.caption || item.alt || item.title || ''
            return url ? { url, caption } : null
          }
          return null
        })
        .filter(Boolean)
    },
    regionTitle() {
      if (!this.$route.params.region) return 'Region'
      return this.$route.params.region
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    filteredTemplates() {
      // First filter out private templates
      const publicTemplates = this.templates.filter(t => {
        const templateType = t.type || 'open' // Default to 'open' if type is not set
        return templateType !== 'private'
      })

      // If region loaded from Firestore
      if (this.regionDoc) {
        const rd = this.regionDoc
        // Filter by continent
        if (rd.regionType === 'continent' && rd.continent) {
          return publicTemplates.filter(t =>
            (t.location?.continent || '').toLowerCase() === rd.continent.toLowerCase()
          )
        }
        // Filter by single country
        if (rd.regionType === 'country' && rd.country) {
          return publicTemplates.filter(t =>
            Array.isArray(t.location?.countries) && t.location.countries.includes(rd.country)
          )
        }
      }
      // Unknown region -> show nothing
      return []
    },
    displayTemplates() {
      const arr = [...this.filteredTemplates]
      return arr.sort((a, b) => a.name.localeCompare(b.name))
    },
    showGrouped() {
      return this.displayTemplates.length > 4
    },
    pavedTemplates() {
      if (!this.showGrouped) return []
      return this.displayTemplates.filter(t => (t?.riding?.difficulty?.rating || 0) === 1)
    },
    gravelTemplates() {
      if (!this.showGrouped) return []
      return this.displayTemplates.filter(t => {
        const r = t?.riding?.difficulty?.rating || 0
        return r === 2 || r === 3
      })
    },
    templateGroups() {
      if (this.showGrouped) {
        const groups = []
        
        // Paved Tours
        if (this.pavedTemplates.length) {
          groups.push({
            groupKey: 'paved',
            title: 'Paved Tours',
            templates: this.pavedTemplates,
            desktopWidthClass: 'width-90-vw',
            photoMarginStyle: 'margin-left: 2vw;',
            badgeStyle: 'height: 10vw; top: 1vw; left: 0',
            titleStyle: 'font-size: 2.8vw'
          })
        }
        
        // Gravel Tours
        if (this.gravelTemplates.length) {
          groups.push({
            groupKey: 'gravel',
            title: 'Paved Tours with some gravel sections',
            templates: this.gravelTemplates,
            desktopWidthClass: 'width-95-vw',
            photoMarginStyle: 'margin-left: 5vw;',
            badgeStyle: 'height: 10vw; top: 2.5vw',
            titleStyle: 'font-size: 2.8vw',
            titleStyleMobile: 'font-size: 6.5vw'
          })
        }
        
        return groups
      } else {
        // Ungrouped: single group with all templates
        return [{
          groupKey: 'all',
          title: null,
          templates: this.displayTemplates,
          desktopWidthClass: 'width-95-vw',
          photoMarginStyle: 'margin-left: 5vw;',
          badgeStyle: 'height: 10vw; top: 2.5vw'
        }]
      }
    },
    headerTitle() {
      const name = this.regionData?.name
      if (!name) return 'Motorcycle Tours'
      return `${name} Motorcycle Tours`
    },
    headerBreadcrumb() {
      const name = this.regionData?.name
      if (!name) return 'Home / Tours'
      return `Home / Tours / ${name}`
    }
  },
  async mounted() {
    await Promise.all([
      this.loadRegion(),
      this.loadTemplates(),
    ])
    this.updateMetadata()
    this.buildRegionImages()
    this.$nextTick(() => emitPrerenderReady())
  },
  watch: {
    '$route'() {
      this.loadRegion()
      this.updateMetadata()
      // Rebuild images for static mapping fallback when route changes
      this.buildRegionImages()
    }
  },
  methods: {
    buildRegionImages() {
      try {
        const images = this.regionData && this.regionData.images
        if (!images) {
          this.regionImagesLocal = []
          return
        }
        const rawArray = Array.isArray(images)
          ? images
          : (typeof images === 'object' ? Object.values(images) : [])
        const mapped = rawArray
          .map(item => {
            if (typeof item === 'string') return { url: item, caption: '' }
            if (item && typeof item === 'object') {
              const url = item.url || item.src || item.image || item.path || ''
              const caption = item.caption || item.alt || item.title || ''
              return url ? { url, caption } : null
            }
            return null
          })
          .filter(Boolean)
        this.regionImagesLocal = mapped
        // Reset index to ensure we are within bounds
        if (this.carouselIndex >= mapped.length) this.carouselIndex = 0
      } catch (e) {
        console.error('Error building region images:', e)
        this.regionImagesLocal = []
        this.carouselIndex = 0
      }
    },
    async loadRegion() {
      try {
        const slug = this.$route.params.region
        if (!slug) { this.regionDoc = null; return }
        const db = getFirestore()
        const regionsRef = collection(db, 'regions')
        const q = query(regionsRef, where('slug', '==', slug), limit(1))
        const snap = await getDocs(q)
        let found = null
        snap.forEach(d => { found = { id: d.id, ...d.data() } })
        this.regionDoc = found
        // Build images when region doc loads
        this.buildRegionImages()
      } catch (e) {
        console.error('Error loading region:', e)
        this.regionDoc = null
      }
    },
    async loadTemplates() {
      try {
        const cacheData = await fetchPublicCache('templates')
        if (cacheData?.data && Array.isArray(cacheData.data)) {
          this.templates = cacheData.data
          return
        }
        console.warn('Public cache for templates missing data, falling back to direct query')
        const db = getFirestore()
        const collectionRef = collection(db, 'templates')
        const querySnapshot = await getDocs(collectionRef)
        const templates = []
        querySnapshot.forEach((docSnap) => {
          const template = docSnap.data()
          template.id = docSnap.id
          templates.push(template)
        })
        this.templates = templates
      } catch (error) {
        console.warn('Public cache for templates fetch failed, falling back to direct query', error)
        try {
          const db = getFirestore()
          const collectionRef = collection(db, 'templates')
          const querySnapshot = await getDocs(collectionRef)
          const templates = []
          querySnapshot.forEach((docSnap) => {
            const template = docSnap.data()
            template.id = docSnap.id
            templates.push(template)
          })
          this.templates = templates
        } catch (inner) {
          console.error('Error loading templates:', inner)
        }
      } finally {
        this.loading = false
      }
    },
    updateMetadata() {
      const regionData = this.regionData
      const title = regionData.title || `${this.regionTitle} Motorcycle Tours`
      const description = regionData.description || `Explore ${this.regionTitle} on our guided motorcycle tours with Renedian Adventure.`
      this.updatePageMeta(title, description)
    },
    viewTemplate(template) {
      const continent = (template.location?.continent || '').toLowerCase().replace(/\s+/g, '-')
      const slug = template.reference?.slug || template.slug
      if (continent && slug) {
        // use global go helper for navigation
        this.go('TourDynamic', { continent, slug })
      }
    },
    difficultyLabel(template) {
      const rating = template?.riding?.difficulty?.rating
      if (rating === 1) return 'Basic'
      if (rating === 2) return 'Intermediate'
      if (rating === 3) return 'Advanced'
      return 'Skill Level'
    },
    getTemplateDescription(template, isMobile = false) {
      if (isMobile && (template.content?.website?.detailedDescription || template.content?.website?.description)) {
        return template.content?.website?.detailedDescription || template.content?.website?.description
      }
      // For desktop, prefer brief, fallback to description
      if (template.content?.website?.brief) {
        return template.content.website.brief
      }
      if (template.content?.website?.description) {
        return template.content.website.description
      }
      return ''
    }
  },
  filters: {
    price(value) {
      if (value === undefined || value === null || isNaN(Number(value))) return ''
      return '$' + Number(value).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },
    kilometers(value) {
      const n = Number(value || 0)
      return n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },
    miles(value) {
      const n = Number(value || 0) * 0.621371
      return n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
  }
}
</script>

<style scoped>
.region-hero-container {
  width: 100vw;
}

@media (min-width: 960px) {
  .region-hero-container {
    width: 70vw;
  }
}

.tour-thumb {
  border: 15px solid white;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.16), 0 6px 12px rgba(0,0,0,0.10);
}

.tour-badge {
  position: absolute;
  width: 18vw;
  left: -4vw;
  bottom: -5vw;
}

.lifted-photo {
  box-shadow:
    0 12px 20px rgba(0, 0, 0, 0.18),
    0 24px 40px rgba(0, 0, 0, 0.12);
}

.explore-button img { transition: opacity 0.2s ease-in-out; }
.explore-button img:hover { cursor: pointer; opacity: 0.6; }

.region-carousel-image {
  border: 15px solid #ffffff;
  box-sizing: border-box;
}

</style>